import { ChangeEventHandler, ReactElement } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';

interface Props {
  label?: string;
  helperText?: string;
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  className?: string;
  required?: boolean;
  error?: string | undefined;
  placeholder?: string;
  disabled?: boolean;
}

const HEX_PATTERN = /^#?[0-9a-fA-F]{0,6}$/;

const normaliseSwatch = (value: string): string => {
  const stripped = value.replace(/^#/, '');
  if (stripped.length === 3 || stripped.length === 6) {
    return `#${stripped}`;
  }
  return 'transparent';
};

export const ColorInput = ({
  label,
  helperText,
  value,
  onChange,
  className = '',
  required = false,
  error,
  placeholder = '#000000',
  disabled,
}: Props): ReactElement => (
  <div className={`flex flex-col ${className}`}>
    {!!label && (
      <label
        htmlFor={label}
        className={`${helperText ? '' : 'mb-2'} font-semibold text-body-medium`}
      >
        {label}
      </label>
    )}
    {!!helperText && (
      <span className="mb-2 mt-1 text-text-low-priority text-sm">
        {helperText}
      </span>
    )}
    <div
      className={classNames(
        'group -outline-offset-1 flex items-center border relative rounded-xs overflow-hidden border-grey-t-700 transition-all duration-100 focus-within:outline-white focus-within:outline-2 bg-background-secondary',
        {
          'border-red-300': error?.length,
          'border-grey-t-700 text-text-disabled': disabled,
          'hover:border-white': !disabled,
        }
      )}
    >
      <input
        className="w-full rounded outline-none disabled:text-text-disabled py-3 leading-none px-3 h-11 font-mono"
        value={value}
        disabled={disabled}
        onChange={(e) => {
          if (HEX_PATTERN.test(e.target.value)) {
            onChange(e);
          }
        }}
        type="text"
        required={required}
        id={label}
        name={label}
        placeholder={placeholder}
      />
      <label
        className="relative shrink-0 size-6 rounded-xs mr-3 border-2 border-white overflow-hidden cursor-pointer"
        style={{ backgroundColor: normaliseSwatch(value) }}
      >
        <input
          type="color"
          className="absolute inset-0 opacity-0 cursor-pointer"
          value={
            normaliseSwatch(value) === 'transparent'
              ? '#000000'
              : normaliseSwatch(value)
          }
          disabled={disabled}
          onChange={onChange}
          aria-label={label ? `${label} picker` : 'Colour picker'}
        />
      </label>
    </div>
    {error && (
      <div className="flex space-x-1.5 items-start mt-2">
        <InformationCircleIcon className="shrink-0 size-4 text-red-300 mt-[3px]" />
        <span className="text-body-smallest text-red-300">{error}</span>
      </div>
    )}
  </div>
);
